import axios, { AxiosRequestConfig, AxiosResponse, AxiosResponseHeaders, Method } from 'axios';
import { saveAs } from 'file-saver';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';
import {
	getErrorMessage,
	parseJSON,
	replaceTemplateValues,
} from 'src/app/modules/utils/FormatUtils';
import { localStorageItem } from 'src/app/modules/utils/LocalStorage';
import { AppContext } from '../../../appContext/AppContext';
import { APP_CONFIGURATION_KEYS, CODE, RESPONSE_MESSAGE } from '../../../utils/Constant';
import { OctTree } from '../../tree';
import { EXPORT_TYPE } from '../constant';
import { PrintDialogProps } from '../types';
import FilePreview from './FilePreview';
import PrintAction from './PrintAction';
import { PDFDocument } from 'pdf-lib';
import { asyncWrapper, replacePathVariablesTemplate } from 'src/app/modules/utils/AppFunction';
import {
	exportStorageFile,
	getSignatureResult,
	getSignatureTree,
	saveStorageFile,
	signFile,
} from 'src/app/modules/utils/ServicesUtils';
import { normalizeSignatureVariable } from '../common';
import fontkit from '@pdf-lib/fontkit';
// Signature service - sử dụng logic code cũ với field5 support
import {
	getSignatureConfig,
	SignatureType,
	SignatureService,
	OTPVerificationDialog,
	initUsbPlugin,
	getCurrentUsbProvider,
} from 'src/app/modules/signature-services';
import { useSSEProgress } from 'src/app/modules/phan-he-bao-hiem-y-te/hooks/useSSEProgress';

const BASE_URL = localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[
	APP_CONFIGURATION_KEYS.API_URL
];
const CURRENT_USER = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);
const COMPONENT_API_MAP = {
	emr: APP_CONFIGURATION_KEYS.API_URL,
	inpatient: APP_CONFIGURATION_KEYS.INPATIENT_URL,
};

const PrintDialog = <T extends object>({
	show,
	onHide,
	title = 'In Phiếu',
	fetchExport,
	params,
	hasSignature,
	signatureParams,
	component = 'emr',
}: PrintDialogProps<T>) => {
	const { setIsLoading, isLoading } = useContext(AppContext);

	const [srcPreview, setSrcPreview] = useState<string>();
	const [treeData, setTreeData] = useState<any[]>([]);
	const [selectedNode, setSelectedNode] = useState<any>();
	const [showOTPDialog, setShowOTPDialog] = useState(false);
	const [caServiceReady, setCaServiceReady] = useState(false);
	const [filePreviewKey, setFilePreviewKey] = useState(0); // Key to force remount FilePreview

	const iframeRef = useRef<HTMLIFrameElement>(null);
	const caServiceRef = useRef<SignatureService | null>(null);
	const fileSignedRef = useRef<Blob | null>(null);
	const fileOriginalRef = useRef<{
		fileName: string;
		file: Blob;
	} | null>(null);
	const signatureVariableRef = useRef('');
	const baseUrlRef = useRef('');
	const aggregateCodeRef = useRef('');
	const aggregateTypeRef = useRef('');
	const aggregateIdRef = useRef('');
	const allSignaturesCompletedRef = useRef(false); // Track if all signatures completed
	const isInitializedRef = useRef(false); // Track if modal has been initialized
	// Pending signature data for CA OTP flow
	const pendingSignatureRef = useRef<{
		pdfDocumentViewer: any;
		signatureVariable: string;
		signatureValue: string;
	} | null>(null);

	const handleFetchActions = async (resultData: any, props: any) => {
		try {
			const config = extractParamsFormConfiguration(resultData);
			const signRequest = config?.signatureConfig?.variables?.find((item: any) => resultData?.signingVariable === item?.code);
			const event = signRequest?.config?.events?.find((item: any) => {
				let condition = false;
				if (item?.triggerConditions?.signatureAction) {
					condition = signRequest?.allowedActions === item?.triggerConditions?.signatureAction
				}
				if (item?.triggerConditions?.signatureStatus) {
					condition = props?.result === item?.triggerConditions?.signatureStatus
				}
				return condition;
			});
			const actions: Promise<AxiosResponse<any, any>>[] = []
			event?.actions?.forEach((item: any) => {
				if (item?.executionTarget === "UI" && item?.actionType === "CALL_API") {
					const request = generateRequestFromConfig(
						{
							...item?.implementation,
							url: item?.implementation?.endpoint
						},
						{
							...resultData,
							[`${resultData?.signingVariable}Signature`]: props?.signatureValue
						},
						{
							baseURL: localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[item?.implementation?.service],
						}
					);
					actions.push(request);
				}
			});

			await Promise.allSettled(actions);
		} catch (error) {
			console.error(error);
		}
	};

	const handleGetSignatureResult = async (jobId: string, data?: any) => {
		try {
			let response = await getSignatureResult(
				baseUrlRef.current,
				jobId,
				{
					aggregateId: data?.aggregateId,
					aggregateType: data?.aggregateType
				}
			);
			if (response?.code === CODE.SUCCESS) {
				await handleFetchActions(response?.data, data);
				await getSignatureTemplateTree(data?.aggregateId);
			}
		} catch (e) {
			console.error(e);
		}
	};

	const handleError = async (message: string, jobId?: string) => { };

	const {
		connect
	} = useSSEProgress({
		baseUrl: baseUrlRef.current,
		progressEndpoint: '/signature/progress',
		onCompleted: handleGetSignatureResult,
		onError: handleError,
		autoClose: true,
	});


	useEffect(() => {
		// Don't run if modal is not shown or params are missing
		if (!show || !params) {
			// Reset initialization flag when modal closes
			if (!show && isInitializedRef.current) {
				isInitializedRef.current = false;
			}
			return;
		}

		// Only initialize once per modal open
		if (isInitializedRef.current) return;
		isInitializedRef.current = true;

		if (hasSignature) {
			baseUrlRef.current =
				localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)[
					COMPONENT_API_MAP[component]
				] ?? BASE_URL;
			getSignatureTemplateTree();
		} else {
			// For non-signature mode, load preview immediately
			onExport(EXPORT_TYPE.PDF, false);
		}
	}, [show, params]);

	const getSignatureTemplateTree = async (nodeSelectedId?: string) => {
		const [data] = await asyncWrapper(
			getSignatureTree(baseUrlRef.current, {
				...signatureParams,
				id: signatureParams?.id,
				objectType: signatureParams?.objectType,
				type: signatureParams?.type,
				module: signatureParams?.module,
			}),
			setIsLoading
		);

		if (data?.length) {
			const treeData = data.map((item: any, index: number) => ({
				...item,
				title: item.aggregateName,
				index,
			}));
			setTreeData(treeData);
			let nodeSelected = nodeSelectedId ? treeData?.find((item: any) => nodeSelectedId === item?.aggregateId) : treeData?.[0]
			onTreeNodeSelect(nodeSelected);
		}
	};

	const onPrint = () => {
		if (iframeRef.current) {
			iframeRef.current.focus();
			iframeRef.current.contentWindow?.print();
		}
	};

	const onExport = async (exportType: string = EXPORT_TYPE.PDF, isExport = true) => {
		try {
			if (hasSignature) {
				onExportWithSignature(exportType);
				return;
			}

			if (!params || !fetchExport) return;

			setIsLoading(true);
			const { data, headers } = await fetchExport({ ...params, fileType: exportType });

			if (!isExport) {
				const URLPreview = URL.createObjectURL(data);
				setSrcPreview(URLPreview);
				fileOriginalRef.current = {
					fileName: getFileName(headers),
					file: data,
				};
				return;
			}

			const fileName = getFileName(headers);
			saveAs(data, fileName);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const onExportWithSignature = async (exportType: string) => {
		try {
			let response:
				| {
						file: Blob;
						fileName: string;
				  }
				| undefined;

			if (EXPORT_TYPE.DOC === exportType) {
				response = await getInitialFile(selectedNode, EXPORT_TYPE.DOC);
			} else {
				response = selectedNode?.storageId
					? await getFileExported(selectedNode)
					: await getInitialFile(selectedNode);
			}

			if (!response) return;

			saveAs(response.file, response.fileName);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	const onSavePdf = async () => {
		try {
			const template = selectedNode?.exportedParamsTemplate;
			if (!template?.value) {
				toast.error('Không tìm thấy cấu hình lưu file');
				return;
			}

			const resolvedValue = replaceTemplateValues(template.value, {
				...selectedNode,
				...signatureParams,
			}) ?? {};

			const formData = new FormData();
			Object.entries(resolvedValue).forEach(([key, value]) => {
				if (key === 'file' || value === undefined || value === null || value === '') return;
				formData.append(key, String(value));
			});
			formData.set(
				'file',
				new File(
					[fileSignedRef.current || fileOriginalRef.current!.file],
					fileOriginalRef.current!.fileName,
					{ type: 'application/pdf' }
				)
			);

			const { code, data } = await saveStorageFile(baseUrlRef.current, formData);

			if (CODE.SUCCESS !== code) {
				toast.error(getErrorMessage({ data }));
				return;
			}

			toast.success('Lưu file thành công');
			if (!fileSignedRef.current) return;

			await signPdfFile(fileSignedRef.current);
		} catch (error) {
			console.error('onSavePdf error:', error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	/**
	 * Check if CA type needs OTP verification
	 * Returns true if CA type and not yet authenticated
	 */
	const checkCANeedsOTP = (config: any): boolean => {
		if (config?.type !== SignatureType.CA) return false;

		// Create CA service if not exists
		if (!caServiceRef.current) {
			caServiceRef.current = SignatureService.fromConfig();
		}

		return !caServiceRef.current.isReady() && !caServiceReady;
	};

	/**
	 * Handle CA OTP request
	 */
	const handleCARequestOTP = async (phoneNumber: string) => {
		if (!caServiceRef.current) {
			caServiceRef.current = SignatureService.fromConfig();
		}

		// Access the underlying signer to call requestOTP
		const signer = (caServiceRef.current as any).signer;
		if (signer && typeof signer.requestOTP === 'function') {
			return await signer.requestOTP(phoneNumber);
		}
		throw new Error('CA provider không hỗ trợ OTP');
	};

	/**
	 * Handle CA OTP verification
	 */
	const handleCAVerifyOTP = async (otp: string, transactionId: string) => {
		if (!caServiceRef.current) return false;

		const signer = (caServiceRef.current as any).signer;
		if (signer && typeof signer.verifyOTP === 'function') {
			const verified = await signer.verifyOTP(otp, transactionId);
			if (verified) {
				setCaServiceReady(true);
			}
			return verified;
		}
		return false;
	};

	/**
	 * Handle CA OTP verified callback - continue with pending signature
	 */
	const handleCAVerified = async () => {
		setCaServiceReady(true);
		toast.success('Xác thực thành công!');

		// Continue with pending signature if exists
		if (pendingSignatureRef.current) {
			const { pdfDocumentViewer, signatureVariable, signatureValue } = pendingSignatureRef.current;
			pendingSignatureRef.current = null; // Clear pending data

			try {
				const signatureConfig = getSignatureConfig();
				await embedAndSignPdf(pdfDocumentViewer, signatureVariable, signatureValue, signatureConfig);
				toast.success('Đã ký số thành công!');
			} catch (error) {
				console.error('Error continuing with pending signature:', error);
				toast.error('Lỗi khi ký số sau xác thực OTP');
			}
		}
	};

	/**
	 * Handle OTP dialog close without verification
	 * Reset preview to remove the signature that was shown before OTP verification
	 */
	const handleOTPDialogClose = () => {
		// Clear pending signature data
		pendingSignatureRef.current = null;

		// Reset preview to original file (remove the signature shown by FilePreview)
		if (fileOriginalRef.current) {
			const urlPreview = URL.createObjectURL(fileOriginalRef.current.file);
			setSrcPreview(urlPreview);
		}

		// Force remount FilePreview to clear its internal signature state
		setFilePreviewKey((prev) => prev + 1);

		// Close the dialog
		setShowOTPDialog(false);
	};

	/**
	 * Sign PDF theo type (phân biệt bởi field5)
	 *
	 * - IMAGE (field5 rỗng): Chỉ embed ảnh chữ ký, không ký số
	 * - USB_TOKEN: Ký trực tiếp qua plugin USB (flow cũ đã hoạt động)
	 * - CA / HSM: Backend xử lý ký số khi gọi signPdfFile() → POST /signature/sign
	 */
	const signPdfWithProvider = async (blob: Blob, config: any): Promise<Blob> => {
		const type = config?.type;

		try {
			switch (type) {
				case SignatureType.IMAGE:
				default:
					return blob;

				case SignatureType.CA:
				case SignatureType.HSM:
					// Backend xử lý ký số khi gọi saveSignedPdfToBackend → POST /signature/sign
					return blob;

				case SignatureType.USB_TOKEN:
					return await signWithUsbToken(blob);
			}
		} catch (error: any) {
			console.error('signPdfWithProvider error:', error);
			if (error?.message?.includes('hủy') || error?.message?.includes('cancel')) {
				toast.warning('Đã hủy ký USB');
			} else {
				toast.warning(`Không thể ký số: ${error?.message || 'Unknown error'}`);
			}
			return blob;
		}
	};

	/**
	 * Ký PDF bằng USB Token plugin (port 14007)
	 */
	const signWithUsbToken = async (pdfBlob: Blob): Promise<Blob> => {
		setIsLoading(true);
		toast.info('Đang khởi tạo USB Token...');

		try {
			const provider = getCurrentUsbProvider();
			initUsbPlugin();

			await new Promise((resolve) => setTimeout(resolve, 2000));

			const signedBlob = await provider.signFileWithFileSignAPI(
				pdfBlob,
				fileOriginalRef.current?.fileName || 'document.pdf',
				(step: string, message: string) => {
					console.log(`[USB Signing] ${step}: ${message}`);
					if (step === 'select_signed') setIsLoading(false);
					if (step === 'upload_ready') setIsLoading(true);
				}
			);

			toast.success('Ký USB Token thành công!');
			return signedBlob;
		} finally {
			setIsLoading(false);
		}
	};

	const onSignature = async (
		pdfDocumentViewer: any,
		signatureVariable: string,
		signatureValue: string
	) => {
		try {
			if (!fileOriginalRef.current) return;

			if (!signatureValue) {
				fileSignedRef.current = null;
				allSignaturesCompletedRef.current = false;
				return;
			}

			signatureVariableRef.current = signatureVariable;

			// Check signature provider first to decide PDF processing strategy
			const signatureConfig = getSignatureConfig();

			// Continue with embedding signature image
			await embedAndSignPdf(pdfDocumentViewer, signatureVariable, signatureValue, signatureConfig);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	/**
	 * Embed signature image into PDF and perform digital signing
	 */
	const embedAndSignPdf = async (
		pdfDocumentViewer: any,
		signatureVariable: string,
		signatureValue: string,
		signatureConfig: any
	) => {
		if (!fileOriginalRef.current) return;

		const pdfDoc = await PDFDocument.load(await fileOriginalRef.current.file.arrayBuffer());
		pdfDoc.registerFontkit(fontkit);
		const pages = pdfDoc.getPages();
		const signatureImg = await pdfDoc.embedPng(signatureValue);
		const fontBytes = await fetch('/pdfjs/Tinos-Regular.ttf').then((res) =>
			res.arrayBuffer()
		);
		const customFont = await pdfDoc.embedFont(fontBytes);
		const totalPage = pdfDocumentViewer.numPages;

		for (let pageNumber = 1; pageNumber <= totalPage; pageNumber++) {
			const page = await pdfDocumentViewer.getPage(pageNumber);
			const pdfTextContent = await page.getTextContent();
			const MARGIN_LEFT = 20,
				OFFSET_HEIGHT = 30;

			pdfTextContent.items.forEach((item: any) => {
				const str: string = item.str;
				const isSignatureVariable = str === signatureVariable;

				if (isSignatureVariable) {
					const [, , , , x, y] = item.transform;
					const currentPage = pages[pageNumber - 1];

					currentPage.drawImage(signatureImg, {
						x,
						y: y - MARGIN_LEFT,
						width: item.width,
						height: item.height + OFFSET_HEIGHT,
					});
					currentPage.drawText(CURRENT_USER.name, {
						x: x + MARGIN_LEFT,
						y: y - item.height - OFFSET_HEIGHT,
						size: 12,
						font: customFont,
					});
				}
			});
		}

		const pdfBytes = await pdfDoc.save();
		let blob = new Blob([pdfBytes as any], { type: 'application/pdf' });

		console.log('PDF with signature image created');

		// Ký số với provider đã cấu hình (USB Token, HSM, CA)
		// Nếu là IMAGE type, signPdfWithProvider sẽ return blob gốc
		blob = await signPdfWithProvider(blob, signatureConfig);

		fileSignedRef.current = blob;

		// Update preview with signed PDF
		const urlPreview = URL.createObjectURL(blob);
		setSrcPreview(urlPreview);

		// Nếu tất cả chữ ký đã hoàn thành (flag được set bởi handleAllSignaturesCompleted)
		// thì gọi API ký số ngay sau khi blob sẵn sàng
		await submitSignedPdfToBackend(blob, signatureConfig, signatureValue);
	};

	const handleAllSignaturesCompleted = async () => {
		console.log('All signatures completed!');

		if (allSignaturesCompletedRef.current) {
			console.log('Already handled, skipping...');
			return;
		}

		// Chỉ set flag - logic gọi API sẽ chạy ở cuối embedAndSignPdf khi blob đã sẵn sàng
		allSignaturesCompletedRef.current = true;
	};

	/**
	 * Convert blob → base64 → gọi API ký số
	 */
	const submitSignedPdfToBackend = async (blob: Blob, signatureConfig: any, signatureValue: any) => {
		try {
			// IMAGE
			if (!signatureConfig.type || signatureConfig.type === SignatureType.IMAGE) {
				toast.success('Đã hoàn tất tất cả chữ ký!');
				return;
			}

			// USB_TOKEN
			if (signatureConfig.type === SignatureType.USB_TOKEN) {
				toast.success('Đã hoàn tất chữ ký! Nhấn "Lưu file PDF" để ký số.');
				return;
			}

			// CA / HSM
			const reader = new FileReader();
			const signedPdfBase64 = await new Promise<string>((resolve, reject) => {
				reader.onloadend = () => {
					const result = reader.result as string;
					const base64Data = result.includes(',') ? result.split(',')[1] : result;
					resolve(base64Data);
				};
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			});

			await saveSignedPdfToBackend(signedPdfBase64, signatureValue);
		} catch (error: any) {
			console.error('Error submitting signed PDF:', error);
			toast.error(`Lỗi khi ký số: ${error?.message || 'Unknown error'}`);
		}
	};

	const saveSignedPdfToBackend = async (signedPdfBase64: string, signatureValue: any) => {
		try {
			toast.info('Đang lưu file đã ký...');

			// Get signature metadata from tree node
			// Normalize signature variable: "${signature_reportingDoctor}" -> "reportingDoctor"
			const normalizedVariable = signatureVariableRef.current
				? normalizeSignatureVariable(signatureVariableRef.current)
				: 'signature';

			const signatureMetadata = {
				aggregateId: aggregateIdRef.current || '',
				aggregateType: aggregateTypeRef.current || '',
				variable: normalizedVariable,
				refferenceId: aggregateIdRef.current || '', // Use same as aggregateId
				aggregateCode: aggregateCodeRef.current || '', // Code bệnh án from tree node
			};

			console.log('Signature metadata:', signatureMetadata);

			// Validate required fields
			if (!signatureMetadata.aggregateId || !signatureMetadata.aggregateType) {
				console.warn('Missing required signature metadata:', signatureMetadata);
				toast.warning('Thiếu thông tin metadata để lưu file ký số');
				return;
			}

			// Validate and clean base64 data
			if (!signedPdfBase64 || signedPdfBase64.trim() === '') {
				console.warn('Empty base64 data');
				toast.warning('Dữ liệu file ký không hợp lệ');
				return;
			}

			// Remove any whitespace and ensure clean base64
			const cleanedBase64 = signedPdfBase64.replace(/\s+/g, '');

			// Prepare API request
			const requestData = {
				signingFiles: [
					{
						fileData: cleanedBase64,
						fileName: fileOriginalRef.current?.fileName || 'signed_document.pdf',
						aggregateId: signatureMetadata.aggregateId,
						aggregateType: signatureMetadata.aggregateType,
						variable: signatureMetadata.variable,
						refferenceId: signatureMetadata.refferenceId,
						aggregateCode: signatureMetadata.aggregateCode,
					},
				],
				signingReason: 'Digital Signature',
			};

			// Call signature API
			const { data } = await axios.post(`${baseUrlRef.current}/signature/async/sign`, requestData, { timeout: 5 * 60000 });
			if (CODE.SUCCESS === data?.code) {
				connect(data?.data, { signatureValue });
			}
		} catch (error: any) {
			console.error('Error saving signed PDF:', error);
			const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
			toast.error(`Lỗi khi lưu file đã ký: ${errorMsg}`);
		}
	};

	const getFileName = (header: AxiosResponseHeaders): string => {
		const contentDisposition = header['content-disposition'];

		if (!contentDisposition) {
			return 'document.pdf'; // Default filename
		}

		const fileName = contentDisposition
			.split(';')
			.find((key) => key.toLowerCase().includes('filename'));

		if (!fileName) return 'document.pdf';

		return fileName.split('=')[1].trim().replaceAll(/[",']/g, '');
	};

	const generateRequestFromConfig = (
		config: {
			method: Method;
			url: string;
			pathVariables?: any;
			payloadTemplate: any;
			requestParameters: any;
		},
		payload: any,
		axiosConfig?: AxiosRequestConfig
	) => {
		const { url: urlTemplate, pathVariables, method, payloadTemplate, requestParameters } = config;
		const url = replacePathVariablesTemplate(urlTemplate, pathVariables, payload);
		const data = replaceTemplateValues(payloadTemplate, payload);
		const params = replaceTemplateValues(requestParameters, payload);
		return axios({
			url,
			method,
			data,
			params,
			...axiosConfig,
		});
	};

	const getFileExported = async (node: any) => {
		try {
			setIsLoading(true);
			const { data: file, headers } = await exportStorageFile(baseUrlRef.current, {
				aggregateId: node.aggregateId,
				aggregateType: node.aggregateType,
			});
			const fileName = getFileName(headers);
			fileOriginalRef.current = {
				fileName,
				file,
			};
			return {
				file,
				fileName,
			};
		} catch (error: any) {
			const errorTextFormatted = await error?.response?.data.text();
			toast.error(getErrorMessage(parseJSON(errorTextFormatted)));
		} finally {
			setIsLoading(false);
		}
	};

	const onTreeNodeSelect = async (node: any) => {
		fileSignedRef.current = null;
		fileOriginalRef.current = null;
		signatureVariableRef.current = '';
		allSignaturesCompletedRef.current = false;
		aggregateCodeRef.current = node?.aggregateCode || '';
		aggregateTypeRef.current = node?.aggregateType || '';
		aggregateIdRef.current = node?.aggregateId || '';

		const config = extractParamsFormConfiguration(node);
		let fileExported: { file: Blob; fileName: string } | undefined = undefined;
		setSelectedNode({
			...node,
			...config,
			// Preserve the original signature boolean flag from API
			signature: node?.signature,
		});

		if (node?.storageId) {
			fileExported = await getFileExported(node);
		} else {
			fileExported = await getInitialFile(node);
		}

		if (!fileExported) return;

		setSrcPreview(URL.createObjectURL(fileExported.file));
	};

	const extractParamsFormConfiguration = (treeNode: any): any => {
		const { additionalConfiguration } = treeNode;

		if (!additionalConfiguration) return null;

		const config = parseJSON(additionalConfiguration);
		const { baseUrl = '', exported, initialExported, signature: signatureConfig } = config ?? {};
		const apiUrl = localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)[baseUrl] ?? BASE_URL;

		const exportedParamsTemplate = exported.formData.find(
			(item: any) => item.module === signatureParams?.module
		);

		return {
			exported,
			initialExported,
			signatureConfig,
			baseUrl: apiUrl,
			exportedParamsTemplate,
		};
	};

	const signPdfFile = async (file: Blob) => {
		const [data] = await asyncWrapper(
			signFile(baseUrlRef.current, {
				aggregateId: selectedNode.aggregateId,
				aggregateType: selectedNode.aggregateType,
				variable: normalizeSignatureVariable(signatureVariableRef.current),
			})
		);

		if (!data) return;

		const nodeDataUpdated = {
			storageId: data?.storageId,
			signedVariables: data?.signedVariables,
			signatureVariables: data?.signatureVariables || [],
		};
		setTreeData(
			treeData.map((item) => {
				if (item.index === selectedNode.index) {
					return {
						...item,
						...nodeDataUpdated,
					};
				}
				return item;
			})
		);
		setSelectedNode({
			...selectedNode,
			...nodeDataUpdated,
		});

		if (data?.storageId) {
			const signedFileData = await getFileExported({
				...selectedNode,
				storageId: data.storageId,
				aggregateId: selectedNode.aggregateId,
				aggregateType: selectedNode.aggregateType,
			});

			if (signedFileData) {
				fileSignedRef.current = signedFileData.file;
				fileOriginalRef.current = {
					fileName: signedFileData.fileName,
					file: signedFileData.file,
				};
				setSrcPreview(URL.createObjectURL(signedFileData.file));
				toast.success('Ký file thành công - đã tải file từ server');
			}
		} else {
			// Fallback: use local file if no storageId
			fileOriginalRef.current = {
				fileName: fileOriginalRef.current!.fileName,
				file,
			};
			fileSignedRef.current = file;
			setSrcPreview(URL.createObjectURL(file));
			toast.success('Ký file thành công');
		}
	};

	const getInitialFile = async (selectedNode: any, exportType = EXPORT_TYPE.PDF) => {
		try {
			const config = extractParamsFormConfiguration(selectedNode);

			if (!config) {
				toast.error('Không tìm thấy cấu hình file');
				setSrcPreview('');
				return;
			}

			setIsLoading(true);
			const getFileRequest = generateRequestFromConfig(
				{
					...config?.initialExported,
					payloadTemplate: {
						...config?.initialExported?.payloadTemplate,
						fileType: exportType,
					},
				},
				selectedNode,
				{
					baseURL: config.baseUrl,
					responseType: 'blob',
				}
			);
			const { data: file, headers } = await getFileRequest;
			const fileName = getFileName(headers);
			fileOriginalRef.current = {
				fileName,
				file,
			};
			return {
				file,
				fileName,
			};
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const uploadSignedFile = async (file: File | Blob) => {
		try {
			setIsLoading(true);
			fileSignedRef.current = file;
			await onSavePdf();
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Modal
				show={show}
				onHide={onHide}
				fullscreen
				scrollable
				className="print-dialog-modal"
				contentClassName="bg-white"
				backdrop="static"
				keyboard={false}
			>
				<Modal.Header className="py-5" closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>

				<Modal.Body className="p-8">
					<div className="d-flex flex-row h-100">
						{hasSignature && (
							<div className="min-w-250px w-25 p-3 border-end">
								<OctTree
									treeData={[
										{
											title: 'Danh sách phiếu',
											subs: treeData,
										},
									]}
									onSelect={(node) => {
										onTreeNodeSelect(node);
									}}
									defaultSelectedId="0-0"
								/>
							</div>
						)}
						<div
							className="spaces flex-grow-1 pb-100"
						>
							{!isLoading && !srcPreview ? (
								<div className="d-flex justify-content-center align-items-center h-100">
									<em className="fs-3 text-gray-600">Không có dữ liệu.</em>
								</div>
							) : (
								<FilePreview
									key={filePreviewKey}
									src={srcPreview}
									ref={iframeRef}
									hasSignature={hasSignature && !!selectedNode?.signature}
									onSignature={onSignature}
									signatureVariables={selectedNode?.signatureVariables}
									signedVariables={selectedNode?.signedVariables}
									onAllSignaturesCompleted={handleAllSignaturesCompleted}
									storageId={selectedNode?.storageId}
								/>
							)}
						</div>
						<div className="min-w-250px p-3 border-start">
							<PrintAction
								onPrint={onPrint}
								onExport={onExport}
								onSave={onSavePdf}
								hasSignature={hasSignature}
								signFile={fileOriginalRef.current}
								uploadSignedFile={uploadSignedFile}
							/>
						</div>
					</div>
				</Modal.Body>

				<Modal.Footer className="position-fixed w-100 bg-white bottom-0 h-auto">
					<Button className="btn btn-danger" onClick={onHide}>
						Đóng
					</Button>
				</Modal.Footer>
			</Modal>

			{/* OTP Verification Dialog for CA signing */}
			<OTPVerificationDialog
				show={showOTPDialog}
				onHide={handleOTPDialogClose}
				onVerified={handleCAVerified}
				providerName={caServiceRef.current?.getSignerInfo?.()?.displayName || 'CA'}
				requestOTP={handleCARequestOTP}
				verifyOTP={handleCAVerifyOTP}
			/>
		</>
	);
};

// Prevent unnecessary re-renders while preserving generic type
export default React.memo(PrintDialog as any, (prevProps, nextProps) => {
	// Don't re-render if modal is closed - keep component mounted
	if (!prevProps.show && !nextProps.show) {
		return true; // Skip re-render when modal is hidden
	}

	// When modal is open, check if the key identifying params changed
	// Support multiple param keys: id, orderGroupId, invoiceId, etc.
	const prevParams = prevProps.params as any;
	const nextParams = nextProps.params as any;

	const prevKey = prevParams?.id || prevParams?.orderGroupId || prevParams?.invoiceId;
	const nextKey = nextParams?.id || nextParams?.orderGroupId || nextParams?.invoiceId;

	const showSame = prevProps.show === nextProps.show;
	const keySame = prevKey === nextKey;
	const signatureSame = prevProps.hasSignature === nextProps.hasSignature;

	return showSame && keySame && signatureSame;
}) as any;
